import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, workAreaAPI } from '../../services/api';

// =============================================================================
// Gig Builder — admin creates CUSTOM gigs of ANY time frame.
// =============================================================================
// A "gig definition" is a reusable template (free-text name, arbitrary start/end
// window incl. overnight, day-of-week recurrence, work-area/city scope, capacity,
// incentive, fee, eligibility gating, guaranteed-minimum, waitlist). The slot
// generator materializes active definitions into per-day bookable templates.

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EMPTY_FORM = {
  name: '',
  description: '',
  startTime: '12:00',
  endTime: '15:00',
  daysOfWeek: [],            // empty = every day
  scope: 'all',
  workAreas: [],
  cities: '',                // comma-separated in the form, array on the wire
  capacity: 15,
  baseIncentive: 30,
  surgeMultiplier: 1.0,
  bookingFee: 0,
  advanceBookingDays: 7,
  earlyAccessDays: 0,
  earlyAccessTier: { minRating: 0, minCompletionRate: 0, minDeliveries: 0 },
  eligibility: { minRating: 0, minCompletionRate: 0, minDeliveries: 0 },
  guaranteedMinimum: { enabled: false, amount: 0, minLoginMinutes: 0, minDeliveries: 0, minAcceptanceRate: 0 },
  checkInWindowMinutes: 15,
  checkInGraceMinutes: 5,
  noShowMinutes: 30,
  geofenceRadiusMeters: 0,
  waitlistEnabled: false,
  waitlistCapacity: 0,
  isActive: true
};

const styles = {
  page: { padding: 24, maxWidth: 1100, margin: '0 auto' },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 20, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  row: { display: 'flex', flexWrap: 'wrap', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', minWidth: 150, flex: '1 1 150px' },
  label: { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 },
  btn: { padding: '9px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnPrimary: { background: '#2563eb', color: '#fff' },
  btnGhost: { background: '#f3f4f6', color: '#374151' },
  btnDanger: { background: '#fee2e2', color: '#b91c1c' },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '18px 0 8px' },
  defItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 8 },
  badge: (active) => ({ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: active ? '#dcfce7' : '#f3f4f6', color: active ? '#15803d' : '#6b7280' }),
  dayChip: (on) => ({ padding: '5px 10px', borderRadius: 6, border: '1px solid', borderColor: on ? '#2563eb' : '#d1d5db', background: on ? '#2563eb' : '#fff', color: on ? '#fff' : '#374151', cursor: 'pointer', fontSize: 13 }),
  banner: (kind) => ({ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14, background: kind === 'error' ? '#fee2e2' : '#dcfce7', color: kind === 'error' ? '#b91c1c' : '#15803d' })
};

function durationLabel(start, end) {
  const tm = (s) => { const [h, m] = s.split(':').map(Number); return h * 60 + m; };
  let diff = tm(end) - tm(start);
  if (diff <= 0) diff += 24 * 60; // overnight
  const h = Math.floor(diff / 60), m = diff % 60;
  return `${h}h${m ? ` ${m}m` : ''}${tm(end) <= tm(start) ? ' (overnight)' : ''}`;
}

export default function GigBuilder() {
  const [definitions, setDefinitions] = useState([]);
  const [workAreas, setWorkAreas] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null); // { kind, text }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [defRes, waRes] = await Promise.all([
        adminAPI.getSlotDefinitions(),
        workAreaAPI.getAllWorkAreas()
      ]);
      setDefinitions(defRes.data?.data?.definitions || []);
      const areas = waRes.data?.data?.workAreas || waRes.data?.workAreas || waRes.data?.data || [];
      setWorkAreas(Array.isArray(areas) ? areas : []);
    } catch (e) {
      setBanner({ kind: 'error', text: e.response?.data?.message || 'Failed to load gig definitions' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const setNested = (group, key, value) => setForm(f => ({ ...f, [group]: { ...f[group], [key]: value } }));
  const toggleDay = (d) => setForm(f => ({
    ...f,
    daysOfWeek: f.daysOfWeek.includes(d) ? f.daysOfWeek.filter(x => x !== d) : [...f.daysOfWeek, d].sort()
  }));
  const toggleWorkArea = (id) => setForm(f => ({
    ...f,
    workAreas: f.workAreas.includes(id) ? f.workAreas.filter(x => x !== id) : [...f.workAreas, id]
  }));

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); };

  const startEdit = (def) => {
    setEditingId(def._id);
    setForm({
      ...EMPTY_FORM,
      ...def,
      cities: (def.cities || []).join(', '),
      workAreas: (def.workAreas || []).map(w => (w._id || w).toString()),
      eligibility: { ...EMPTY_FORM.eligibility, ...(def.eligibility || {}) },
      guaranteedMinimum: { ...EMPTY_FORM.guaranteedMinimum, ...(def.guaranteedMinimum || {}) }
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setBanner({ kind: 'error', text: 'Gig name is required' }); return; }
    if (form.startTime === form.endTime) { setBanner({ kind: 'error', text: 'Start and end time cannot be identical' }); return; }
    if (form.scope === 'specific' && form.workAreas.length === 0) { setBanner({ kind: 'error', text: 'Pick at least one work area for a scoped gig' }); return; }

    const payload = {
      ...form,
      capacity: Number(form.capacity),
      baseIncentive: Number(form.baseIncentive),
      surgeMultiplier: Number(form.surgeMultiplier),
      bookingFee: Number(form.bookingFee),
      advanceBookingDays: Number(form.advanceBookingDays),
      earlyAccessDays: Number(form.earlyAccessDays),
      earlyAccessTier: {
        minRating: Number(form.earlyAccessTier.minRating),
        minCompletionRate: Number(form.earlyAccessTier.minCompletionRate),
        minDeliveries: Number(form.earlyAccessTier.minDeliveries)
      },
      checkInWindowMinutes: Number(form.checkInWindowMinutes),
      checkInGraceMinutes: Number(form.checkInGraceMinutes),
      noShowMinutes: Number(form.noShowMinutes),
      geofenceRadiusMeters: Number(form.geofenceRadiusMeters),
      waitlistCapacity: Number(form.waitlistCapacity),
      cities: form.cities.split(',').map(c => c.trim()).filter(Boolean),
      eligibility: {
        minRating: Number(form.eligibility.minRating),
        minCompletionRate: Number(form.eligibility.minCompletionRate),
        minDeliveries: Number(form.eligibility.minDeliveries)
      },
      guaranteedMinimum: {
        enabled: !!form.guaranteedMinimum.enabled,
        amount: Number(form.guaranteedMinimum.amount),
        minLoginMinutes: Number(form.guaranteedMinimum.minLoginMinutes),
        minDeliveries: Number(form.guaranteedMinimum.minDeliveries),
        minAcceptanceRate: Number(form.guaranteedMinimum.minAcceptanceRate)
      }
    };

    setSaving(true);
    try {
      if (editingId) {
        await adminAPI.updateSlotDefinition(editingId, payload);
        setBanner({ kind: 'success', text: 'Gig definition updated' });
      } else {
        await adminAPI.createSlotDefinition(payload);
        setBanner({ kind: 'success', text: 'Gig definition created' });
      }
      resetForm();
      await load();
    } catch (e) {
      setBanner({ kind: 'error', text: e.response?.data?.message || 'Failed to save gig definition' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (def) => {
    try { await adminAPI.toggleSlotDefinition(def._id); await load(); }
    catch (e) { setBanner({ kind: 'error', text: e.response?.data?.message || 'Toggle failed' }); }
  };

  const handleDelete = async (def) => {
    if (!window.confirm(`Delete gig "${def.name}"? Already-generated slots keep their bookings; future generation just won't include it.`)) return;
    try { await adminAPI.deleteSlotDefinition(def._id); await load(); }
    catch (e) { setBanner({ kind: 'error', text: e.response?.data?.message || 'Delete failed' }); }
  };

  const num = (key, label, opts = {}) => (
    <div style={styles.field}>
      <span style={styles.label}>{label}</span>
      <input type="number" style={styles.input} value={form[key]} min={opts.min} max={opts.max} step={opts.step || 1}
        onChange={e => set(key, e.target.value)} />
    </div>
  );

  return (
    <div style={styles.page}>
      <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Gig Builder</h1>
      <p style={{ color: '#6b7280', marginBottom: 20 }}>Create custom gigs of any time frame. After creating/editing gigs, run <b>Generate Templates</b> in Slot Management to publish bookable slots.</p>

      {banner && <div style={styles.banner(banner.kind)}>{banner.text}</div>}

      {/* ---- Builder form ---- */}
      <div style={styles.card}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>{editingId ? 'Edit gig' : 'New gig'}</h2>

        <div style={styles.row}>
          <div style={{ ...styles.field, flex: '2 1 260px' }}>
            <span style={styles.label}>Name *</span>
            <input style={styles.input} value={form.name} placeholder="e.g. Lunch Rush, Full Day, Late Night Express"
              onChange={e => set('name', e.target.value)} />
          </div>
          <div style={{ ...styles.field, flex: '3 1 320px' }}>
            <span style={styles.label}>Description</span>
            <input style={styles.input} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
        </div>

        <div style={styles.sectionTitle}>Time frame</div>
        <div style={styles.row}>
          <div style={styles.field}>
            <span style={styles.label}>Start time</span>
            <input type="time" style={styles.input} value={form.startTime} onChange={e => set('startTime', e.target.value)} />
          </div>
          <div style={styles.field}>
            <span style={styles.label}>End time</span>
            <input type="time" style={styles.input} value={form.endTime} onChange={e => set('endTime', e.target.value)} />
          </div>
          <div style={{ ...styles.field, justifyContent: 'flex-end' }}>
            <span style={styles.label}>Duration</span>
            <div style={{ padding: '8px 10px', fontWeight: 600, color: '#2563eb' }}>{durationLabel(form.startTime, form.endTime)}</div>
          </div>
        </div>

        <div style={styles.sectionTitle}>Recurrence (leave empty = every day)</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {DAYS.map((d, i) => (
            <button key={d} type="button" style={styles.dayChip(form.daysOfWeek.includes(i))} onClick={() => toggleDay(i)}>{d}</button>
          ))}
        </div>

        <div style={styles.sectionTitle}>Scope</div>
        <div style={styles.row}>
          <div style={styles.field}>
            <span style={styles.label}>Applies to</span>
            <select style={styles.input} value={form.scope} onChange={e => set('scope', e.target.value)}>
              <option value="all">All work areas</option>
              <option value="specific">Specific work areas</option>
            </select>
          </div>
          <div style={{ ...styles.field, flex: '3 1 320px' }}>
            <span style={styles.label}>City filter (optional, comma-separated)</span>
            <input style={styles.input} value={form.cities} placeholder="e.g. Mumbai, Pune" onChange={e => set('cities', e.target.value)} />
          </div>
        </div>
        {form.scope === 'specific' && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {workAreas.length === 0 && <span style={{ color: '#9ca3af', fontSize: 13 }}>No work areas found.</span>}
            {workAreas.map(wa => (
              <button key={wa._id} type="button" style={styles.dayChip(form.workAreas.includes(wa._id.toString()))}
                onClick={() => toggleWorkArea(wa._id.toString())}>{wa.name} {wa.city ? `· ${wa.city}` : ''}</button>
            ))}
          </div>
        )}

        <div style={styles.sectionTitle}>Capacity & economics</div>
        <div style={styles.row}>
          {num('capacity', 'Capacity', { min: 1, max: 1000 })}
          {num('baseIncentive', 'Base incentive (₹)', { min: 0 })}
          {num('surgeMultiplier', 'Surge ×', { min: 1, max: 5, step: 0.05 })}
          {num('bookingFee', 'Booking fee (₹, 0 = free)', { min: 0 })}
        </div>

        <div style={styles.sectionTitle}>Booking window & eligibility</div>
        <div style={styles.row}>
          {num('advanceBookingDays', 'Advance booking (days)', { min: 1, max: 30 })}
        </div>
        <div style={styles.row}>
          <div style={styles.field}><span style={styles.label}>Min rating</span>
            <input type="number" style={styles.input} min={0} max={5} step={0.1} value={form.eligibility.minRating} onChange={e => setNested('eligibility', 'minRating', e.target.value)} /></div>
          <div style={styles.field}><span style={styles.label}>Min completion %</span>
            <input type="number" style={styles.input} min={0} max={100} value={form.eligibility.minCompletionRate} onChange={e => setNested('eligibility', 'minCompletionRate', e.target.value)} /></div>
          <div style={styles.field}><span style={styles.label}>Min deliveries</span>
            <input type="number" style={styles.input} min={0} value={form.eligibility.minDeliveries} onChange={e => setNested('eligibility', 'minDeliveries', e.target.value)} /></div>
        </div>

        <div style={styles.sectionTitle}>Tiered early access (optional)</div>
        <div style={styles.row}>
          {num('earlyAccessDays', 'Early-access head start (days)', { min: 0, max: 14 })}
          <div style={{ ...styles.field, flex: '3 1 280px', justifyContent: 'flex-end' }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Priority-tier riders (meeting the bar below) can book this many days before the standard window opens. 0 = no early access.</span>
          </div>
        </div>
        {Number(form.earlyAccessDays) > 0 && (
          <div style={styles.row}>
            <div style={styles.field}><span style={styles.label}>Tier min rating</span>
              <input type="number" style={styles.input} min={0} max={5} step={0.1} value={form.earlyAccessTier.minRating} onChange={e => setNested('earlyAccessTier', 'minRating', e.target.value)} /></div>
            <div style={styles.field}><span style={styles.label}>Tier min completion %</span>
              <input type="number" style={styles.input} min={0} max={100} value={form.earlyAccessTier.minCompletionRate} onChange={e => setNested('earlyAccessTier', 'minCompletionRate', e.target.value)} /></div>
            <div style={styles.field}><span style={styles.label}>Tier min deliveries</span>
              <input type="number" style={styles.input} min={0} value={form.earlyAccessTier.minDeliveries} onChange={e => setNested('earlyAccessTier', 'minDeliveries', e.target.value)} /></div>
          </div>
        )}

        <div style={styles.sectionTitle}>Guaranteed minimum earnings</div>
        <div style={styles.row}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.guaranteedMinimum.enabled} onChange={e => setNested('guaranteedMinimum', 'enabled', e.target.checked)} />
            <span style={{ fontSize: 14 }}>Enable guarantee</span>
          </label>
        </div>
        {form.guaranteedMinimum.enabled && (
          <div style={styles.row}>
            <div style={styles.field}><span style={styles.label}>Floor (₹)</span>
              <input type="number" style={styles.input} min={0} value={form.guaranteedMinimum.amount} onChange={e => setNested('guaranteedMinimum', 'amount', e.target.value)} /></div>
            <div style={styles.field}><span style={styles.label}>Min login (min)</span>
              <input type="number" style={styles.input} min={0} value={form.guaranteedMinimum.minLoginMinutes} onChange={e => setNested('guaranteedMinimum', 'minLoginMinutes', e.target.value)} /></div>
            <div style={styles.field}><span style={styles.label}>Min deliveries</span>
              <input type="number" style={styles.input} min={0} value={form.guaranteedMinimum.minDeliveries} onChange={e => setNested('guaranteedMinimum', 'minDeliveries', e.target.value)} /></div>
            <div style={styles.field}><span style={styles.label}>Min acceptance %</span>
              <input type="number" style={styles.input} min={0} max={100} value={form.guaranteedMinimum.minAcceptanceRate} onChange={e => setNested('guaranteedMinimum', 'minAcceptanceRate', e.target.value)} /></div>
          </div>
        )}

        <div style={styles.sectionTitle}>Attendance & waitlist</div>
        <div style={styles.row}>
          {num('checkInWindowMinutes', 'Check-in window (± min)', { min: 0, max: 240 })}
          {num('checkInGraceMinutes', 'Auto-checkout grace (min)', { min: 0, max: 120 })}
          {num('noShowMinutes', 'No-show after (min)', { min: 5, max: 360 })}
          {num('geofenceRadiusMeters', 'Geofence: require inside work area (>0 = on)', { min: 0, max: 20000 })}
        </div>
        <div style={styles.row}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.waitlistEnabled} onChange={e => set('waitlistEnabled', e.target.checked)} />
            <span style={{ fontSize: 14 }}>Enable waitlist</span>
          </label>
          {form.waitlistEnabled && num('waitlistCapacity', 'Waitlist capacity (0 = unlimited)', { min: 0 })}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
            <span style={{ fontSize: 14 }}>Active</span>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button style={{ ...styles.btn, ...styles.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleSave}>
            {saving ? 'Saving…' : (editingId ? 'Update gig' : 'Create gig')}
          </button>
          {editingId && <button style={{ ...styles.btn, ...styles.btnGhost }} onClick={resetForm}>Cancel edit</button>}
        </div>
      </div>

      {/* ---- Existing definitions ---- */}
      <div style={styles.card}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Existing gigs ({definitions.length})</h2>
        {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}
        {!loading && definitions.length === 0 && <p style={{ color: '#9ca3af' }}>No gigs yet. Create one above. (Until you do, generation falls back to the legacy 6-slot preset.)</p>}
        {definitions.map(def => (
          <div key={def._id} style={styles.defItem}>
            <div>
              <div style={{ fontWeight: 700 }}>
                {def.name} <span style={styles.badge(def.isActive)}>{def.isActive ? 'ACTIVE' : 'INACTIVE'}</span>
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                {def.startTime}–{def.endTime} · {durationLabel(def.startTime, def.endTime)} ·{' '}
                {(!def.daysOfWeek || def.daysOfWeek.length === 0) ? 'Every day' : def.daysOfWeek.map(d => DAYS[d]).join(', ')} ·{' '}
                cap {def.capacity} · ₹{def.baseIncentive} {def.surgeMultiplier > 1 ? `×${def.surgeMultiplier}` : ''}{' '}
                {def.bookingFee > 0 ? `· fee ₹${def.bookingFee}` : '· free'}{' '}
                {def.scope === 'specific' ? `· ${(def.workAreas || []).length} area(s)` : '· all areas'}
                {def.guaranteedMinimum?.enabled ? ` · guarantee ₹${def.guaranteedMinimum.amount}` : ''}
                {def.waitlistEnabled ? ' · waitlist' : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...styles.btn, ...styles.btnGhost }} onClick={() => startEdit(def)}>Edit</button>
              <button style={{ ...styles.btn, ...styles.btnGhost }} onClick={() => handleToggle(def)}>{def.isActive ? 'Deactivate' : 'Activate'}</button>
              <button style={{ ...styles.btn, ...styles.btnDanger }} onClick={() => handleDelete(def)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
